import { ReactNode } from "react";

import Typography from "../typography";
import { cn } from "../../utls/utils";

export type ThemedFormSectionGap = "sm" | "md" | "lg";

export interface ThemedFormSectionProps {
  /**
   * Section heading displayed at the top of the block.
   */
  title?: ReactNode;
  /**
   * Supporting copy rendered under the heading.
   */
  description?: ReactNode;
  /**
   * Optional slot rendered to the right of the title (e.g., actions or help links).
   */
  action?: ReactNode;
  /**
   * Footer area rendered below the fields. Useful for helper text.
   */
  footer?: ReactNode;
  /**
   * Number of columns used for the section content.
   */
  columns?: 1 | 2 | 3;
  /**
   * Gap size between fields in the section.
   */
  gap?: ThemedFormSectionGap;
  /**
   * Class names applied to the outer wrapper.
   */
  className?: string;
  /**
   * Class names applied to the content grid.
   */
  contentClassName?: string;
  children: ReactNode;
}

const columnClasses: Record<NonNullable<ThemedFormSectionProps["columns"]>, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-3",
};

const gapClasses: Record<ThemedFormSectionGap, string> = {
  sm: "gap-3",
  md: "gap-4",
  lg: "gap-6",
};

export function ThemedFormSection({
  title,
  description,
  action,
  footer,
  columns = 1,
  gap = "md",
  className,
  contentClassName,
  children,
}: ThemedFormSectionProps) {
  return (
    <section
      className={cn(
        "rounded-xl border border-cream-200 bg-cream-50 p-6 shadow-sm",
        className
      )}
    >
      {(title || description || action) && (
        <div className="flex flex-col gap-3 border-b border-cream-200 pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            {typeof title === "string" ? (
              <Typography variant="subtitle1" className="text-brown-700">
                {title}
              </Typography>
            ) : (
              title ?? null
            )}
            {typeof description === "string" ? (
              <Typography variant="body2" className="text-brown-600">
                {description}
              </Typography>
            ) : (
              description ?? null
            )}
          </div>
          {action ? <div className="shrink-0 text-sm text-terracotta-700">{action}</div> : null}
        </div>
      )}

      <div
        className={cn(
          "mt-4 grid",
          columnClasses[columns],
          gapClasses[gap],
          contentClassName
        )}
      >
        {children}
      </div>

      {footer ? (
        <div className="mt-4 border-t border-cream-200 pt-4 text-sm text-brown-600">
          {footer}
        </div>
      ) : null}
    </section>
  );
}

export default ThemedFormSection;
